import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Minus, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "../context/CartContext";
import { cocartApi, getCheckoutUrl } from "../lib/api";
import { beep } from "../lib/sound";

export default function CartDrawer() {
  const {
    items,
    isCartOpen,
    closeCart,
    setQty,
    removeItem,
    subtotal,
  } = useCart();
  const [isCheckingOut, setIsCheckingOut] = useState(false);

  const handleCheckout = async () => {
    if (items.length === 0) return;
    setIsCheckingOut(true);
    beep(980, 0.1, 0.05);

    try {
      // Clear the server cart first
      await cocartApi.post("/cart/clear");
      
      // Add all items from our local cart
      for (const item of items) {
        await cocartApi.post("/cart/add-item", {
          id: item.product.id.toString(),
          quantity: item.qty.toString()
        });
      }

      // Fetch the cart to get the cart_key
      const cartResponse = await cocartApi.get("/cart");
      const cartKey = cartResponse.data.cart_key;

      // Redirect to WordPress checkout passing the cart session
      window.location.href = getCheckoutUrl(cartKey);
    } catch (error) {
      console.error("Checkout error:", error);
      toast.error("CHECKOUT FAILED", {
        description: "Failed to connect to the store backend. Please try again."
      });
      setIsCheckingOut(false);
    }
  };

  return (
    <AnimatePresence>
      {isCartOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeCart}
            className="fixed inset-0 z-[75] bg-black/70 backdrop-blur-sm"
            data-testid="cart-backdrop"
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 260, damping: 30 }}
            data-testid="cart-drawer"
            className="fixed bottom-0 right-0 top-0 z-[80] flex w-full max-w-md flex-col border-l border-cyanx/30 bg-abyss"
          >
            <div className="flex items-center justify-between border-b border-cyanx/20 px-6 py-5">
              <div>
                <h2 className="font-display text-lg font-bold text-ink">
                  CARGO HOLD
                </h2>
                <p className="font-mono text-[9px] tracking-[0.35em] text-mute">
                  SECURED ARTIFACTS // GUMZ LINE
                </p>
              </div>
              <button
                data-testid="cart-close-button"
                onClick={closeCart}
                aria-label="Close cart"
                className="btn-y2k p-2"
                disabled={isCheckingOut}
              >
                <X size={16} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4">
              {items.length === 0 && (
                <div
                  data-testid="cart-empty-state"
                  className="mt-16 text-center"
                >
                  <p className="anim-blink font-mono text-xs tracking-[0.4em] text-mute">
                    CARGO HOLD EMPTY
                  </p>
                  <p className="mt-3 font-mono text-[11px] text-fog">
                    THE TRAIN WAITS FOR NO ONE.
                  </p>
                </div>
              )}
              {items.map(({ product, qty }) => (
                <div
                  key={product.id}
                  data-testid={`cart-item-${product.id}`}
                  className="mb-4 flex gap-4 border border-cyanx/15 bg-panel p-3"
                >
                  <img
                    src={product.image}
                    alt={product.name}
                    className="h-20 w-20 shrink-0 border border-cyanx/20 object-cover saturate-[0.85]"
                  />
                  <div className="flex flex-1 flex-col justify-between">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="font-display text-xs font-bold text-ink">
                          {product.name}
                        </p>
                        <p className="font-mono text-[10px] text-fog">
                          ${product.price} / UNIT
                        </p>
                      </div>
                      <button
                        data-testid={`cart-remove-${product.id}`}
                        onClick={() => {
                          beep(360, 0.06);
                          removeItem(product.id);
                        }}
                        aria-label={`Remove ${product.name}`}
                        className="btn-y2k p-1.5"
                        disabled={isCheckingOut}
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1">
                        <button
                          data-testid={`cart-qty-minus-${product.id}`}
                          onClick={() => setQty(product.id, qty - 1)}
                          className="btn-y2k px-2 py-1"
                          aria-label="Decrease quantity"
                          disabled={isCheckingOut}
                        >
                          <Minus size={12} />
                        </button>
                        <span
                          data-testid={`cart-qty-${product.id}`}
                          className="w-8 text-center font-mono text-xs text-ink"
                        >
                          {qty}
                        </span>
                        <button
                          data-testid={`cart-qty-plus-${product.id}`}
                          onClick={() => setQty(product.id, qty + 1)}
                          className="btn-y2k px-2 py-1"
                          aria-label="Increase quantity"
                          disabled={isCheckingOut}
                        >
                          <Plus size={12} />
                        </button>
                      </div>
                      <span className="font-mono text-sm font-bold text-hazard">
                        ${product.price * qty}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-cyanx/20 px-6 py-5">
              <div className="mb-4 flex items-center justify-between font-mono">
                <span className="text-[11px] tracking-[0.3em] text-fog">
                  SUBTOTAL
                </span>
                <span
                  data-testid="cart-subtotal"
                  className="text-glow-yellow text-xl font-bold text-hazard"
                >
                  ${subtotal}
                </span>
              </div>
              <button
                data-testid="checkout-open-button"
                disabled={items.length === 0 || isCheckingOut}
                onClick={handleCheckout}
                className="btn-y2k w-full py-4 font-mono text-xs font-bold tracking-[0.3em]"
              >
                {isCheckingOut ? "SYNCING TO TERMINAL..." : "INITIATE CHECKOUT PROTOCOL"}
              </button>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
